// Is the market open? (Ben, 25 Oct 2026.) Display only: which label a stock price gets ("Live", "Closed ·
// Fri close", "Lunch break"), never which price is used. Worked out in each exchange's own time zone with
// Intl, so daylight saving in New York is handled. There's no holiday calendar: on a weekday when the
// exchange should be open, a latest price from an earlier date (once the session is 30 minutes old) means
// the market is closed today.

export type Market = 'SGX' | 'US';
export type MarketPhase = 'open' | 'closed' | 'break';
export interface MarketState {
  market: Market;
  state: MarketPhase;
  /** The exchange-local date (YYYY-MM-DD) of the session the latest price and its change belong to. */
  session: string;
  /** When the current session segment ends (open) or trading resumes (break), as a moment. */
  closeAt?: number;
  /** When the market next opens (closed or break), as a moment. Holidays aren't known ahead. */
  opensAt?: number;
}

const ZONES: Record<Market, string> = { SGX: 'Asia/Singapore', US: 'America/New_York' };
/** Trading segments, minutes after local midnight. SGX breaks for lunch from 12:00 to 13:00. */
const SEGMENTS: Record<Market, [number, number][]> = { SGX: [[540, 720], [780, 1020]], US: [[570, 960]] };
const US_EXCH = ['Nasdaq', 'NYSE', 'NYSE Arca', 'NYSE American', 'Cboe', 'OTC', 'US'];
const GRACE = 30 * 60e3;

/** The market an exchange name belongs to (as kept on a stock), or null for anything else. */
export function marketOf(exch: string | null | undefined): Market | null {
  if (exch === 'SGX') return 'SGX';
  return exch && US_EXCH.includes(exch) ? 'US' : null;
}

const fmtCache = new Map<string, Intl.DateTimeFormat>();
function parts(ms: number, tz: string) {
  let f = fmtCache.get(tz);
  if (!f) { f = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23', weekday: 'short' }); fmtCache.set(tz, f); }
  const p = f.formatToParts(new Date(ms)), g = (t: string) => p.find((x) => x.type === t)!.value;
  const y = +g('year'), m = +g('month'), d = +g('day');
  return { y, m, d, hh: +g('hour'), mi: +g('minute'), ss: +g('second'), dow: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(g('weekday')), date: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` };
}
/** The moment at a local wall-clock time in a time zone (the offset is found from the zone itself). */
function zoned(date: string, minutes: number, tz: string): number {
  const [y, m, d] = date.split('-').map(Number), wall = Date.UTC(y, m - 1, d, Math.floor(minutes / 60), minutes % 60);
  let t = wall;
  for (let i = 0; i < 3; i++) { const p = parts(t, tz), seen = Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mi, p.ss); t += wall - seen; }
  return t;
}
const addDays = (date: string, n: number) => { const [y, m, d] = date.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10); };
const dowOf = (date: string) => { const [y, m, d] = date.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)).getUTCDay(); };
const weekday = (date: string) => { const w = dowOf(date); return w >= 1 && w <= 5; };
/** The latest weekday on or before a date. */
const lastWeekday = (date: string) => { let d = date; while (!weekday(d)) d = addDays(d, -1); return d; };
/** The first session start after a moment (weekdays only). */
function nextOpen(market: Market, date: string, minutes: number): number {
  const tz = ZONES[market], segs = SEGMENTS[market];
  let d = date;
  for (let i = 0; i < 8; i++) {
    if (weekday(d)) for (const [a] of segs) if (d !== date || a > minutes) return zoned(d, a, tz);
    d = addDays(d, 1);
  }
  return zoned(d, segs[0][0], tz);
}

/**
 * Where a market stands at a moment. lastPriceAt (optional) is when the latest price was taken: on a weekday
 * that should be trading, a price from an earlier date 30 minutes into the session marks a holiday.
 */
export function marketState(market: Market, now: number, lastPriceAt?: number | null): MarketState {
  const tz = ZONES[market], segs = SEGMENTS[market], p = parts(now, tz), mins = p.hh * 60 + p.mi + p.ss / 60, today = p.date;
  const lastDate = lastPriceAt ? parts(lastPriceAt, tz).date : null;
  if (weekday(today)) {
    const open = segs.find(([a, b]) => mins >= a && mins < b);
    const inBreak = !open && mins >= segs[0][0] && mins < segs[segs.length - 1][1];
    const holiday = (open || inBreak) && lastDate != null && lastDate < today && now - zoned(today, segs[0][0], tz) > GRACE;
    if (open && !holiday) return { market, state: 'open', session: today, closeAt: zoned(today, open[1], tz) };
    if (inBreak && !holiday) { const resume = segs.find(([a]) => a > mins)!; return { market, state: 'break', session: today, opensAt: zoned(today, resume[0], tz), closeAt: zoned(today, resume[0], tz) }; }
    let session = mins >= segs[segs.length - 1][1] ? today : lastWeekday(addDays(today, -1));
    if (holiday) session = lastDate!;
    if (lastDate && lastDate < session) session = lastDate;
    return { market, state: 'closed', session, opensAt: nextOpen(market, holiday ? addDays(today, 1) : today, holiday ? -1 : mins) };
  }
  let session = lastWeekday(today);
  if (lastDate && lastDate < session) session = lastDate;
  return { market, state: 'closed', session, opensAt: nextOpen(market, today, mins) };
}
